import {
  ForwardRefRenderFunction,
  forwardRef,
  useImperativeHandle,
  useState,
} from 'react'

import { useHealthToolsCache } from '@hhgtech/hhg-components'
import {
  LOCALE_HAS_COMMUNITY_SITE,
  QUERY_HEALTH_TOOL,
} from '@hhgtech/hhg-components/constants'
import { DatePicker, Text } from '@hhgtech/hhg-components/mantine'
import dayjs from 'dayjs'
import { useIntl } from 'react-intl'

import { redirectToSSOWithReturnUrl } from 'api/utils/redirects'
import { DueDateInfoType } from 'components/pages/healthTools/due-date'
import { useAppContext } from 'hooks/useAppContext'
import { useRouterExtend } from 'hooks/useRouterExtend'
import { API_DATE_FORMAT, basePath } from 'interfaces/constants'
import { HEALTH_TOOL_TYPE } from 'interfaces/constants/healthTools'

import { FormControl, StyledLastPeriodFormContainer } from './index.styled'
import {
  DueDateCalculatorType,
  getDueDateConception,
  useDueDateStore,
} from './utils'

type Props = {
  onSubmit?: (info: DueDateInfoType, redirectLink?: string) => void
  isEmbed?: boolean
  controlledSubmit?: boolean
}

export type HandlerProps = {
  submitForm: any
}

const DueDateFormConception: ForwardRefRenderFunction<HandlerProps, Props> = (
  { onSubmit, isEmbed = false, controlledSubmit },
  ref,
) => {
  const { formatMessage: f } = useIntl()
  const { locale, router } = useRouterExtend()
  const {
    state: { isLoggedIn },
  } = useAppContext()
  const [conceptionDate, setConceptionDate] = useState(new Date())

  const { getHealthToolLink } = useHealthToolsCache(locale)
  const { setIsCalculating } = useDueDateStore()
  useImperativeHandle(ref, () => ({
    submitForm: onSubmitForm,
  }))

  const onSubmitForm = async () => {
    const currentUrl = isEmbed
      ? `${window.location.origin}${basePath}${getHealthToolLink(
          HEALTH_TOOL_TYPE.DUE_DATE,
        )}`
      : window.location.origin + window.location.pathname

    const params = [
      `${QUERY_HEALTH_TOOL.type}=${DueDateCalculatorType.conception}`,
      `${QUERY_HEALTH_TOOL.startDate}=${dayjs(conceptionDate).format(
        API_DATE_FORMAT,
      )}`,
      `${QUERY_HEALTH_TOOL.additional}= `,
      LOCALE_HAS_COMMUNITY_SITE.includes(locale) ? 'joincommunity=1' : '',
    ]
      .filter(Boolean)
      .join('&')
    const redirectLink = currentUrl + `?${params}`

    if (controlledSubmit) {
      setIsCalculating(true)
      const info = await getDueDateConception(conceptionDate)
      onSubmit && onSubmit(info, redirectLink)

      return
    }

    if (!isLoggedIn) {
      redirectToSSOWithReturnUrl(redirectLink, 'due-date')
    }
    if (isEmbed) {
      router.push(redirectLink)
    } else {
      setIsCalculating(true)
      const info = await getDueDateConception(conceptionDate)
      onSubmit && onSubmit(info, redirectLink)
    }
  }

  return (
    <StyledLastPeriodFormContainer>
      <FormControl>
        <DatePicker
          label={
            <Text as="label" size="s4">
              {f({
                id: 'dueDatePage.conceptionDate',
              })}
            </Text>
          }
          value={conceptionDate}
          size="md"
          onChange={(value) => {
            setConceptionDate(value || new Date())
          }}
          excludeDate={(date) =>
            dayjs(date).isBefore(dayjs().subtract(43, 'week'))
          }
        />
      </FormControl>
    </StyledLastPeriodFormContainer>
  )
}

export default forwardRef(DueDateFormConception)
